import Close from '@mui/icons-material/Close';
import { inputBaseClasses, outlinedInputClasses } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import IconButton from '@mui/material/IconButton';
import InputAdornment, { inputAdornmentClasses } from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { SxProps, Theme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { OrderUnitOfMeasure } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { get, startCase } from 'lodash-es';
import { useMemo } from 'react';
import { FieldValues, useController, UseControllerProps } from 'react-hook-form';

import { getUomDisplayOverride } from '~constants/enums';
import { useAllowedOrderUnitOfMeasures } from '~hooks/useAllowedUnitOfMeasureOptions';

import Label from './Label';

const unitKeys = Object.keys(OrderUnitOfMeasure) as (keyof typeof OrderUnitOfMeasure)[];
const allUnitOfMeasureOptions = unitKeys.map((unit) => {
  const value = OrderUnitOfMeasure[unit];

  return {
    label: getUomDisplayOverride(unit) ?? startCase(unit.toLowerCase()),
    value,
  };
});

export type QuantityAndUnitInputValue = {
  quantity: number;
  uom: { label: string; value: string };
};

type Props<T extends FieldValues = QuantityAndUnitInputValue> = UseControllerProps<T> & {
  isDisabled?: boolean;
  isRequired?: boolean;
  label?: React.ReactNode;
  sx?: SxProps<Theme>;
};

export default function QuantityAndUnitInput<
  T extends FieldValues = { [key: string]: QuantityAndUnitInputValue | null },
>({ control, isDisabled, isRequired, label, name, sx }: Props<T>) {
  const allowedUnits = useAllowedOrderUnitOfMeasures();

  const unitOfMeasureOptions = useMemo(() => {
    if (allowedUnits.length === 0) return allUnitOfMeasureOptions;
    return allUnitOfMeasureOptions.filter(({ value }) => allowedUnits.includes(value));
  }, [allowedUnits]);

  const { field, fieldState } = useController({ control, name });

  const quantityError =
    get(fieldState.error, 'quantity.message') ??
    get(fieldState.error, 'root.quantity.message');
  const uomError =
    get(fieldState.error, 'uom.message') ?? get(fieldState.error, 'root.uom.message');

  const hasSelectedUom = Boolean(field.value?.uom);

  return (
    <FormControl
      disabled={isDisabled}
      error={Boolean(quantityError || uomError)}
      fullWidth
      sx={[
        { display: 'flex', flexDirection: 'column', gap: 0.25 },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {label && <Label isRequired={isRequired}>{label}</Label>}

      <TextField
        className={isRequired ? 'required' : ''}
        error={Boolean(quantityError || uomError)}
        fullWidth
        id={name}
        onChange={(event) => {
          field.onChange({ ...field.value, quantity: event.target.value });
        }}
        onKeyDown={(event) => {
          if (event.key === 'e' || event.key === 'E') {
            event.preventDefault();
          }
        }}
        type="number"
        value={field.value?.quantity ?? null}
        slotProps={{
          input: {
            endAdornment: (
              <InputAdornment position="end" sx={{ position: 'relative' }}>
                <Select
                  error={Boolean(uomError)}
                  onChange={(event: SelectChangeEvent) => {
                    const selectedUom = unitOfMeasureOptions.find((uom) => {
                      return uom.value === event.target.value;
                    });

                    field.onChange({ ...field.value, uom: selectedUom ?? null });
                  }}
                  value={field.value?.uom?.value ?? ''}
                  sx={(theme) => ({
                    [`&.${inputBaseClasses.root}`]: {
                      bgcolor: theme.semantic.surfaceSunken,
                      borderBottomLeftRadius: 0,
                      borderLeft: 'solid 1px rgba(0, 0, 0, 0.23)',
                      borderTopLeftRadius: 0,
                      width: '150px',
                    },
                    [`.${inputBaseClasses.input}`]: {
                      fontSize: '12px',
                      p: '8px 14px',
                      pr: '40px !important',
                    },
                    [`.${outlinedInputClasses.notchedOutline}`]: {
                      border: '0 !important',
                    },
                  })}
                >
                  {unitOfMeasureOptions.map((uom) => (
                    <MenuItem key={uom.value} value={uom.value}>
                      {uom.label}
                    </MenuItem>
                  ))}
                </Select>

                {hasSelectedUom ? (
                  <IconButton
                    onClick={() => field.onChange({ ...field.value, uom: null })}
                    size="small"
                    sx={(theme) => ({
                      height: '24px',
                      position: 'absolute',
                      right: '30px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: '24px',
                      '&:hover svg': { color: theme.brandV2.colors.treadRed },
                    })}
                  >
                    <Close sx={{ fontSize: '18px' }} />
                  </IconButton>
                ) : (
                  <Typography
                    sx={(theme) => ({
                      color: theme.brandV2.colors.treadGray1,
                      fontSize: '12px',
                      left: '14px',
                      pointerEvents: 'none',
                      position: 'absolute',
                      top: '50%',
                      transform: 'translateY(-50%)',
                    })}
                  >
                    {t('form_fields.select_unit_placeholder')}
                  </Typography>
                )}
              </InputAdornment>
            ),
          },
        }}
        sx={{
          [`.${inputBaseClasses.root}`]: {
            pr: 0,
            [`&.${inputBaseClasses.focused} .${inputAdornmentClasses.root} .${inputBaseClasses.root}`]:
              { borderLeftColor: 'rgba(0, 0, 0, 0.23)' },
          },
          [`&:hover .${inputAdornmentClasses.root} .${inputBaseClasses.root}`]: {
            borderLeftColor: 'rgba(0, 0, 0, 0.87)',
          },
        }}
      />

      {(quantityError || uomError) && (
        <FormHelperText sx={{ m: 0, fontSize: '10px', fontWeight: 700 }}>
          {[
            quantityError ? `${quantityError}.` : '',
            uomError ? `${uomError}.` : '',
          ].join(' ')}
        </FormHelperText>
      )}
    </FormControl>
  );
}
